import React, { useEffect, useMemo, useState } from "react";
import { FaPlus, FaTrash } from "react-icons/fa";
import { useNotify } from "../../components/ConfirmModal";
import payablesApi, { payablesError } from "./payablesApi";

export const formatMoney = (v) =>
  (Number(v) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function formatDate(value) {
  if (!value) return "—";
  const [y, m, d] = String(value).slice(0, 10).split("-").map(Number);
  if (!y) return "—";
  return new Date(y, m - 1, d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

export function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function FinanceModal({ title, onClose, children, footer }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="viewlead-modal-overlay" onClick={onClose}>
      <div
        className="viewlead-modal-content finance-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="viewlead-modal-header">
          <h3 className="viewlead-modal-title">{title}</h3>
          <button type="button" className="viewlead-modal-close" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>
        {children}
        {footer ? <div className="viewlead-modal-actions finance-reminder-actions">{footer}</div> : null}
      </div>
    </div>
  );
}

const VENDOR_FIELDS = [
  "Name",
  "Contact_Person",
  "Email",
  "Phone",
  "GSTIN",
  "PAN",
  "Default_Category",
  "Payment_Terms_Days",
  "Address",
  "Payment_Details",
  "Notes",
  "Status",
];

/** Add or edit a vendor (supplier who sends us bills). */
export function VendorModal({ vendor, onClose, onSaved }) {
  const notify = useNotify();
  const [form, setForm] = useState(() =>
    Object.fromEntries(VENDOR_FIELDS.map((k) => [k, vendor?.[k] ?? (k === "Status" ? "Active" : "")]))
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const saved = vendor
        ? await payablesApi.updateVendor(vendor.Vendor_Id, form)
        : await payablesApi.createVendor(form);
      notify.success(vendor ? "Vendor updated" : "Vendor added");
      onSaved(saved);
    } catch (err) {
      setError(payablesError(err, "Could not save the vendor"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <FinanceModal
      title={vendor ? `Edit vendor · ${vendor.Name}` : "New vendor"}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-clear" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="vendor-form" className="btn-primary btn-add-lead" disabled={saving}>
            {saving ? "Saving…" : "Save vendor"}
          </button>
        </>
      }
    >
      {error ? <div className="finance-form-error">{error}</div> : null}
      <form id="vendor-form" className="finance-form-grid" onSubmit={save}>
        <label className="finance-span-2">
          Vendor name *
          <input value={form.Name} onChange={set("Name")} required maxLength={255} placeholder="e.g. Hostinger, AWS, office landlord" />
        </label>
        <label>
          Contact person
          <input value={form.Contact_Person} onChange={set("Contact_Person")} maxLength={150} />
        </label>
        <label>
          Email
          <input type="email" value={form.Email} onChange={set("Email")} />
        </label>
        <label>
          Phone
          <input value={form.Phone} onChange={set("Phone")} maxLength={20} />
        </label>
        <label>
          Usual category
          <input value={form.Default_Category} onChange={set("Default_Category")} maxLength={100} placeholder="Hosting, Software, Rent…" />
        </label>
        <label>
          GSTIN
          <input value={form.GSTIN} onChange={set("GSTIN")} maxLength={15} />
        </label>
        <label>
          PAN
          <input value={form.PAN} onChange={set("PAN")} maxLength={10} />
        </label>
        <label>
          Pay within (days)
          <input type="number" min={0} max={365} value={form.Payment_Terms_Days} onChange={set("Payment_Terms_Days")} />
          <span className="finance-form-hint">Used to fill in the due date of new bills.</span>
        </label>
        {vendor ? (
          <label>
            Status
            <select value={form.Status} onChange={set("Status")}>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </label>
        ) : null}
        <label className="finance-span-2">
          Address
          <textarea rows={2} value={form.Address} onChange={set("Address")} />
        </label>
        <label className="finance-span-2">
          How we pay them
          <textarea rows={2} value={form.Payment_Details} onChange={set("Payment_Details")} placeholder="Bank account, IFSC or UPI ID" />
        </label>
        <label className="finance-span-2">
          Notes
          <textarea rows={2} value={form.Notes} onChange={set("Notes")} />
        </label>
      </form>
    </FinanceModal>
  );
}

const emptyItem = () => ({ Description: "", Quantity: "1", Unit_Price: "", Tax_Percentage: "" });

function itemTotals(items) {
  return items.reduce(
    (acc, it) => {
      const base = (Number(it.Quantity) || 0) * (Number(it.Unit_Price) || 0);
      const tax = it.Tax_Percentage === "" ? 0 : (base * (Number(it.Tax_Percentage) || 0)) / 100;
      return { sub: acc.sub + base, tax: acc.tax + tax };
    },
    { sub: 0, tax: 0 }
  );
}

/** Add or edit a vendor bill. Once paid on, only due date, category and notes can change. */
export function BillModal({ billId, vendors, options, onClose, onSaved }) {
  const notify = useNotify();
  const [bill, setBill] = useState(null);
  const [form, setForm] = useState(() => ({
    Vendor_Id: "",
    Bill_No: "",
    Bill_Date: todayIso(),
    Due_Date: "",
    Category: "",
    Currency_Id: "",
    Notes: "",
  }));
  const [items, setItems] = useState([emptyItem()]);
  const [loading, setLoading] = useState(Boolean(billId));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!billId) return undefined;
    let alive = true;
    payablesApi
      .bill(billId)
      .then((b) => {
        if (!alive) return;
        setBill(b);
        setForm({
          Vendor_Id: String(b.Vendor_Id),
          Bill_No: b.Bill_No || "",
          Bill_Date: b.Bill_Date || "",
          Due_Date: b.Due_Date || "",
          Category: b.Category || "",
          Currency_Id: b.Currency_Id ? String(b.Currency_Id) : "",
          Notes: b.Notes || "",
        });
        setItems(
          b.items?.length
            ? b.items.map((it) => ({
                Description: it.Description,
                Quantity: it.Quantity,
                Unit_Price: it.Unit_Price,
                Tax_Percentage: it.Tax_Percentage ?? "",
              }))
            : [{ ...emptyItem(), Description: b.Category || "Bill amount", Unit_Price: b.Total_Amount }]
        );
      })
      .catch((err) => alive && setError(payablesError(err, "Could not load the bill")))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [billId]);

  const locked = Boolean(bill && (Number(bill.Paid_Amount) > 0 || bill.payments?.some((p) => p.Payment_Status !== "Cancelled" && p.Payment_Status !== "Failed")));
  const cancelled = bill?.Status === "Cancelled";
  const isDraft = !bill || bill.Status === "Draft";
  const totals = useMemo(() => itemTotals(items), [items]);
  const activeVendors = vendors.filter((v) => v.Status === "Active" || String(v.Vendor_Id) === form.Vendor_Id);

  const set = (k) => (e) => {
    const value = e.target.value;
    setForm((f) => {
      const next = { ...f, [k]: value };
      if (k === "Vendor_Id" && !bill) {
        const v = vendors.find((x) => String(x.Vendor_Id) === value);
        if (v?.Default_Category && !f.Category) next.Category = v.Default_Category;
      }
      return next;
    });
  };
  const setItem = (i, k) => (e) =>
    setItems((list) => list.map((it, idx) => (idx === i ? { ...it, [k]: e.target.value } : it)));

  const save = async (status) => {
    setSaving(true);
    setError("");
    const body = locked
      ? { Due_Date: form.Due_Date || null, Category: form.Category, Notes: form.Notes }
      : {
          ...form,
          Vendor_Id: Number(form.Vendor_Id) || null,
          Currency_Id: form.Currency_Id ? Number(form.Currency_Id) : null,
          Due_Date: form.Due_Date || null,
          items: items.filter((it) => String(it.Description).trim() || Number(it.Unit_Price)),
          ...(status ? { Status: status } : {}),
        };
    try {
      const saved = bill ? await payablesApi.updateBill(bill.Bill_Id, body) : await payablesApi.createBill(body);
      notify.success(bill ? "Bill updated" : status === "Draft" ? "Draft saved" : "Bill added");
      onSaved(saved);
    } catch (err) {
      setError(payablesError(err, "Could not save the bill"));
    } finally {
      setSaving(false);
    }
  };

  const title = bill
    ? `Bill ${bill.Bill_No || `#${bill.Bill_Id}`} · ${bill.Vendor_Name || ""}`
    : "New bill to pay";

  const footer = cancelled ? (
    <button type="button" className="btn-clear" onClick={onClose}>
      Close
    </button>
  ) : (
    <>
      <button type="button" className="btn-clear" onClick={onClose}>
        Cancel
      </button>
      {isDraft ? (
        <button type="button" className="btn-secondary-lead" disabled={saving || loading} onClick={() => save("Draft")}>
          Save as draft
        </button>
      ) : null}
      <button
        type="button"
        className="btn-primary btn-add-lead"
        disabled={saving || loading}
        onClick={() => save(isDraft ? "Unpaid" : undefined)}
      >
        {saving ? "Saving…" : isDraft ? "Save bill" : "Save changes"}
      </button>
    </>
  );

  return (
    <FinanceModal title={title} onClose={onClose} footer={footer}>
      {error ? <div className="finance-form-error">{error}</div> : null}
      {loading ? (
        <p className="finance-form-hint">Loading…</p>
      ) : (
        <>
          {locked && !cancelled ? (
            <p className="finance-aging-hint">
              Payments are recorded on this bill, so only the due date, category and notes can change.
            </p>
          ) : null}
          {cancelled ? (
            <p className="finance-form-error">Cancelled: {bill.Cancel_Reason || "no reason given"}</p>
          ) : null}
          <div className="finance-form-grid">
            <label>
              Vendor *
              <select value={form.Vendor_Id} onChange={set("Vendor_Id")} disabled={locked || cancelled} required>
                <option value="">Choose a vendor…</option>
                {activeVendors.map((v) => (
                  <option key={v.Vendor_Id} value={String(v.Vendor_Id)}>
                    {v.Name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Vendor's bill number
              <input value={form.Bill_No} onChange={set("Bill_No")} disabled={locked || cancelled} maxLength={100} />
            </label>
            <label>
              Bill date *
              <input type="date" value={form.Bill_Date} onChange={set("Bill_Date")} disabled={locked || cancelled} required />
            </label>
            <label>
              Pay by
              <input type="date" value={form.Due_Date} onChange={set("Due_Date")} disabled={cancelled} />
              <span className="finance-form-hint">Leave empty to use the vendor's usual payment terms.</span>
            </label>
            <label>
              Category
              <input value={form.Category} onChange={set("Category")} disabled={cancelled} maxLength={100} placeholder="Hosting, Software, Rent…" />
            </label>
            <label>
              Currency
              <select value={form.Currency_Id} onChange={set("Currency_Id")} disabled={locked || cancelled}>
                <option value="">Default (INR)</option>
                {options.currencies.map((c) => (
                  <option key={c.id} value={String(c.id)}>
                    {c.code}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <table className="finance-items-table" style={{ marginTop: 14 }}>
            <thead>
              <tr>
                <th>What it's for</th>
                <th style={{ width: 70 }}>Qty</th>
                <th style={{ width: 110 }}>Rate</th>
                <th style={{ width: 80 }}>Tax %</th>
                <th style={{ width: 32 }} />
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i}>
                  <td>
                    <input value={it.Description} onChange={setItem(i, "Description")} disabled={locked || cancelled} />
                  </td>
                  <td>
                    <input type="number" min={0} step="0.01" value={it.Quantity} onChange={setItem(i, "Quantity")} disabled={locked || cancelled} />
                  </td>
                  <td>
                    <input type="number" min={0} step="0.01" value={it.Unit_Price} onChange={setItem(i, "Unit_Price")} disabled={locked || cancelled} />
                  </td>
                  <td>
                    <input type="number" min={0} max={100} step="0.01" value={it.Tax_Percentage} onChange={setItem(i, "Tax_Percentage")} disabled={locked || cancelled} />
                  </td>
                  <td>
                    {!locked && !cancelled && items.length > 1 ? (
                      <button
                        type="button"
                        className="accounts-payable-action-btn"
                        aria-label="Remove line"
                        onClick={() => setItems((list) => list.filter((_, idx) => idx !== i))}
                      >
                        <FaTrash size={12} />
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!locked && !cancelled ? (
            <button type="button" className="btn-clear" style={{ marginTop: 8 }} onClick={() => setItems((l) => [...l, emptyItem()])}>
              <FaPlus size={11} /> Add line
            </button>
          ) : null}
          <div className="finance-items-total">
            Subtotal {formatMoney(totals.sub)} · Tax {formatMoney(totals.tax)} ·{" "}
            <strong>Total {formatMoney(totals.sub + totals.tax)}</strong>
            {bill && Number(bill.Paid_Amount) > 0 ? (
              <>
                {" "}· Paid {formatMoney(bill.Paid_Amount)} · <strong>Left to pay {formatMoney(bill.Balance_Amount)}</strong>
              </>
            ) : null}
          </div>

          <div className="finance-form-grid" style={{ marginTop: 12 }}>
            <label className="finance-span-2">
              Notes
              <textarea rows={2} value={form.Notes} onChange={set("Notes")} disabled={cancelled} />
            </label>
          </div>

          {bill?.payments?.length ? (
            <>
              <h4 style={{ margin: "14px 0 6px", fontSize: 14 }}>Payments made</h4>
              <table className="finance-items-table">
                <thead>
                  <tr>
                    <th>Paid on</th>
                    <th>Amount</th>
                    <th>From</th>
                    <th>Mode</th>
                    <th>Reference</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {bill.payments.map((p) => (
                    <tr key={p.Payment_Id}>
                      <td>{formatDate(p.Payment_Date)}</td>
                      <td>{formatMoney(p.Amount)}</td>
                      <td>{p.Company_Account_Name || "—"}</td>
                      <td>{p.Payment_Mode_Name || "—"}</td>
                      <td>{p.Transaction_Ref || "—"}</td>
                      <td>{p.Payment_Status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : null}
        </>
      )}
    </FinanceModal>
  );
}

/** Record money paid out against a bill. */
export function PayBillModal({ bill, options, onClose, onSaved }) {
  const notify = useNotify();
  const [form, setForm] = useState({
    Amount: bill.Balance_Amount,
    Payment_Date: todayIso(),
    Company_Account_Id: options.accounts.length === 1 ? String(options.accounts[0].id) : "",
    Payment_Mode_Id: "",
    Transaction_Ref: "",
    Notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const res = await payablesApi.recordPayment({
        ...form,
        Bill_Id: bill.Bill_Id,
        Company_Account_Id: form.Company_Account_Id ? Number(form.Company_Account_Id) : null,
        Payment_Mode_Id: form.Payment_Mode_Id ? Number(form.Payment_Mode_Id) : null,
      });
      notify.success(res?.bill?.Status === "Paid" ? "Bill fully paid" : "Payment recorded");
      onSaved(res);
    } catch (err) {
      setError(payablesError(err, "Could not record the payment"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <FinanceModal
      title={`Pay bill ${bill.Bill_No || `#${bill.Bill_Id}`} · ${bill.Vendor_Name || ""}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-clear" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="pay-bill-form" className="btn-primary btn-add-lead" disabled={saving}>
            {saving ? "Saving…" : "Record payment"}
          </button>
        </>
      }
    >
      {error ? <div className="finance-form-error">{error}</div> : null}
      <p className="finance-aging-hint">
        Left to pay: <strong>{formatMoney(bill.Balance_Amount)}</strong>
        {bill.Due_Date ? ` · pay by ${formatDate(bill.Due_Date)}` : ""}
      </p>
      <form id="pay-bill-form" className="finance-form-grid" onSubmit={save}>
        <label>
          Amount paid *
          <input type="number" min={0.01} step="0.01" max={bill.Balance_Amount} value={form.Amount} onChange={set("Amount")} required />
        </label>
        <label>
          Paid on *
          <input type="date" value={form.Payment_Date} onChange={set("Payment_Date")} required />
        </label>
        <label>
          Paid from (our account)
          <select value={form.Company_Account_Id} onChange={set("Company_Account_Id")}>
            <option value="">Not specified</option>
            {options.accounts.map((a) => (
              <option key={a.id} value={String(a.id)}>
                {a.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Payment mode
          <select value={form.Payment_Mode_Id} onChange={set("Payment_Mode_Id")}>
            <option value="">Not specified</option>
            {options.modes.map((m) => (
              <option key={m.id} value={String(m.id)}>
                {m.name}
              </option>
            ))}
          </select>
        </label>
        <label className="finance-span-2">
          Reference (UTR / cheque no.)
          <input value={form.Transaction_Ref} onChange={set("Transaction_Ref")} maxLength={150} />
        </label>
        <label className="finance-span-2">
          Notes
          <textarea rows={2} value={form.Notes} onChange={set("Notes")} />
        </label>
      </form>
    </FinanceModal>
  );
}

/** Void a bill that was entered by mistake (needs a reason, and no payments on it). */
export function CancelBillModal({ bill, onClose, onSaved }) {
  const notify = useNotify();
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const saved = await payablesApi.cancelBill(bill.Bill_Id, reason);
      notify.success("Bill cancelled");
      onSaved(saved);
    } catch (err) {
      setError(payablesError(err, "Could not cancel the bill"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <FinanceModal
      title={`Cancel bill ${bill.Bill_No || `#${bill.Bill_Id}`}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-clear" onClick={onClose}>
            Keep bill
          </button>
          <button type="submit" form="cancel-bill-form" className="btn-primary btn-add-lead" disabled={saving || !reason.trim()}>
            {saving ? "Cancelling…" : "Cancel bill"}
          </button>
        </>
      }
    >
      {error ? <div className="finance-form-error">{error}</div> : null}
      <form id="cancel-bill-form" className="finance-form-grid" onSubmit={save}>
        <label className="finance-span-2">
          Why is this bill being cancelled? *
          <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} required placeholder="Entered twice, wrong vendor…" />
        </label>
      </form>
    </FinanceModal>
  );
}
